/**
 * Source of truth for which Handlebars variables a given PDF template
 * may reference. Tied to the context object the renderer in
 * oo-job-service passes into Handlebars when compiling the template body.
 *
 * When adding a new variable to the renderer, also list it here so it
 * shows up in the editor's variable panel.
 */
export interface PdfTemplateVar {
	name: string;
	description: string;
	/** When true, must be used with triple-brace ({{{var}}}) to render as HTML. */
	raw?: boolean;
}

export const PDF_TEMPLATE_NAMES = {
	JOB_CONFIRMATION_CUSTOMER: "JOB_CONFIRMATION_CUSTOMER",
	JOB_CONFIRMATION_CONSULTANT: "JOB_CONFIRMATION_CONSULTANT",
} as const;

const SHARED_VARS: PdfTemplateVar[] = [
	{ name: "job.orderNumber", description: "Ordernummer för uppdraget" },
	{
		name: "job.startDate",
		description: 'Uppdragets startdatum (använd t.ex. {{moment job.startDate "YYYY-MM-DD"}})',
	},
	{ name: "job.description", description: "Uppdragsbeskrivning" },
	{ name: "job.customerDescription", description: "Beskrivning av arbetsplatsen" },
	{ name: "job.customerOrderNumber", description: "Kundens beställningsnummer (om angivet)" },
	{ name: "job.purchaseOrderNo", description: "Inköpsordernummer (om angivet)" },
	{ name: "job.invoiceReference", description: "Fakturareferens för uppdraget (om angiven)" },
	{ name: "job.address.address1", description: "Gatuadress för uppdraget" },
	{ name: "job.address.postalCode", description: "Postnummer för uppdraget" },
	{ name: "job.address.city", description: "Ort för uppdraget" },
	{ name: "customer.name", description: "Kundens namn" },
	{ name: "customer.customerNumber", description: "Kundnummer" },
	{ name: "service", description: "Kompetensens tjänstebeteckning" },
	{ name: "skills", description: "Kommaseparerad lista av valda kompetenser" },
	{
		name: "shiftsDisplay",
		description:
			"Array med en formaterad rad per pass. Använd med {{#each shiftsDisplay}}<span>{{this}}</span><br />{{/each}}.",
	},
];

export const PDF_TEMPLATE_VARS: Record<string, PdfTemplateVar[]> = {
	[PDF_TEMPLATE_NAMES.JOB_CONFIRMATION_CUSTOMER]: SHARED_VARS,
	[PDF_TEMPLATE_NAMES.JOB_CONFIRMATION_CONSULTANT]: [
		...SHARED_VARS,
		{ name: "consultant.firstName", description: "Konsultens förnamn" },
		{ name: "consultant.lastName", description: "Konsultens efternamn" },
		{ name: "consultant.email", description: "Konsultens e-postadress" },
		{ name: "contactPerson.firstName", description: "Kontaktpersonens förnamn" },
		{ name: "contactPerson.lastName", description: "Kontaktpersonens efternamn" },
		{ name: "contactPerson.email", description: "Kontaktpersonens e-post" },
		{ name: "contactPerson.profile.phoneNumber", description: "Kontaktpersonens telefon" },
	],
};

export function isPdfTemplateName(name: string): boolean {
	return name in PDF_TEMPLATE_VARS;
}
